// CODE-BASED routes (DEV-GUIDE-002 §4 house choice — module subtrees compose
// from packages, so file-based routing's one-app-owns-the-tree assumption
// doesn't hold; code-based hand-writes .lazy() + createLazyRoute).
//
// DOCUMENTED CHOICE — the root layout gates NOTHING. Auth gating is
// per-screen: /login must render for anonymous users, future public screens
// (password reset, invite acceptance) must not fight a global gate, and each
// screen's data needs differ. screens/home gates itself via useSession().
//
// The root now renders <App/> (app.tsx) instead of a bare <Outlet/>: App draws
// the shell chrome — sidebar, header, footer — and renders the Outlet inside
// it. That is a PRESENTATION change only; App gates nothing, it just skips the
// chrome on /login so the door still renders bare for anonymous visitors.
import { createRootRouteWithContext, createRoute, createRouter } from "@tanstack/solid-router";
import type { RouterHistory } from "@tanstack/solid-router";
import type { QueryClient } from "@tanstack/solid-query";
import { NotFoundCard } from "@erp/ui/error-card";
import { parseSearch, stringifySearch } from "@erp/ui/route-kit";
import { App } from "./app";
import { LoginScreen } from "./screens/login";
import {
  makeLibOverviewRoute,
  makeLibCatalogueRoutes,
  makeLibPatronsRoutes,
  makeLibMembershipsRoutes,
  makeLibSettingsRoutes,
  makeLibLocationsRoutes,
  makeLibAcquisitionsRoutes,
  makeLibDigitalRoutes,
  makeLibOperationsRoutes,
  makeLibReportsRoutes,
} from "@erp/module-lib/fe/routes/admin";
import { makeCmsRoutes } from "@erp/module-cms/fe/routes/admin";
import { makeAuditRoutes } from "@erp/module-audit/fe/routes/admin";
import { makeFinanceRoutes } from "@erp/module-fin/fe/routes/admin";
import { makeAuthStaffRoutes } from "@erp/module-auth/fe/routes/admin";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: App,
});

/** The portal's root route type — the parent handle module route subtrees hang off of. */
export type AdminRootRoute = typeof rootRoute;

// /login is EAGER — it is the entry screen; splitting it would add a
// round-trip exactly where first paint matters most.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginScreen,
});

// / is the lazy chunk — screens/home closes the loop with
// `export const Route = createLazyRoute('/')({ component: HomeScreen })`.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
}).lazy(() => import("./screens/home").then((m) => m.Route));

// The LIB consoles compose in here — each factory builds its subtree
// (`/library/catalogue`, `/library/patrons`) + lazy screens against THIS root
// route. They are independent siblings; adding patrons never touches catalogue.
// /scanner — the PHONE camera page. PUBLIC and chromeless: the phone has no
// session and never signs in (the relay's phone ops are unauthenticated by
// design), so app.tsx lists it alongside /login as a bare route.
const scannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scanner",
}).lazy(() => import("./screens/scanner").then((m) => m.Route));

// /setup — the chromeless first-run wizard (lib:admin gated in-screen).
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
}).lazy(() => import("./screens/setup").then((m) => m.Route));

// /profile — the signed-in operator's own identity + grants view.
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
}).lazy(() => import("./screens/profile").then((m) => m.Route));

// /verify — where a printed card's QR lands when opened by a plain camera app.
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify",
}).lazy(() => import("./screens/verify").then((m) => m.Route));

// /accept-invite — where a staff invitation email lands. PUBLIC and chromeless
// (the whole point is that the recipient has no account yet). No validateSearch:
// the screen reads the two opaque strings off the URL and the SERVER decides
// whether they mean anything, so a client-side schema here would only turn a
// bad link into a router error instead of the one refusal sentence.
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
}).lazy(() => import("./screens/accept-invite").then((m) => m.Route));

// /reset-password — the forgotten-password flow. PUBLIC and chromeless for the
// same reason /login is: the person using it has no session, and getting one back
// is the entire point. The code and the new password are submitted TOGETHER, so
// there is no intermediate "this address was verified" credential to protect.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
}).lazy(() => import("./screens/reset-password").then((m) => m.Route));

const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  indexRoute,
  scannerRoute,
  verifyRoute,
  acceptInviteRoute,
  profileRoute,
  setupRoute,
  ...makeLibOverviewRoute(rootRoute),
  ...makeLibCatalogueRoutes(rootRoute),
  ...makeLibPatronsRoutes(rootRoute),
  ...makeLibMembershipsRoutes(rootRoute),
  ...makeLibSettingsRoutes(rootRoute),
  ...makeLibLocationsRoutes(rootRoute),
  ...makeLibAcquisitionsRoutes(rootRoute),
  ...makeLibDigitalRoutes(rootRoute),
  ...makeLibOperationsRoutes(rootRoute),
  ...makeLibReportsRoutes(rootRoute),
  ...makeCmsRoutes(rootRoute),
  ...makeFinanceRoutes(rootRoute),
  ...makeAuditRoutes(rootRoute),
  ...makeAuthStaffRoutes(rootRoute),
]);

/** The unknown-route page: the shared NotFoundCard as the page (h1), with Home as the way out. */
function RouteNotFound() {
  return (
    <NotFoundCard
      id="route-not-found"
      what="page"
      heading
      hint="Check the address, or pick a console from the sidebar."
    />
  );
}

/** Router factory — main.tsx passes the app QueryClient; tests pass a memory history. */
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    defaultPreload: "intent", // hover/focus warms the chunk (and loader data, when loaders land)
    defaultPreloadStaleTime: 0, // let Query own staleness, not the router (DEV-GUIDE-002 §4.3)
    // An unknown path renders INSIDE the root's outlet — so inside the shell,
    // with the sidebar as the way back — as the same NotFoundCard every detail
    // screen uses, with an h1 and a Home link. TanStack's default was the bare
    // words "Not Found": no heading, no link, no chrome (audit UX-10).
    defaultNotFoundComponent: RouteNotFound,
    // The house search codec (audit UX-17): primitives travel as typed —
    // `?trashed=1`, never `?trashed=%221%22` — so a generated URL and a
    // hand-typed or shared one agree; each route's validateSearch coerces.
    parseSearch,
    stringifySearch,
    context: { queryClient },
    ...(history ? { history } : {}),
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/solid-router" {
  interface Register {
    router: AppRouter;
  }
}
